import React, { useEffect, useContext, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Typography, Modal, Form, Input, Button, Radio, Divider, Table, Checkbox, Select, Space, Tag, Tooltip, message, Tabs, Alert, Result } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { AuthContext, NavBarContext, AuthInfoContext, ConfigContext } from '../App';
import { PauseCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, FileAddOutlined, QuestionCircleOutlined, SyncOutlined, ClockCircleOutlined, CloseCircleOutlined, EyeOutlined, FileTextOutlined, ArrowLeftOutlined, ArrowRightOutlined, FileExcelOutlined, ArrowUpOutlined, ArrowDownOutlined, BorderOuterOutlined, ColumnWidthOutlined, CreditCardOutlined } from '@ant-design/icons';
import './Bookings.css';
const DEV_ENDPOINT = 'http://localhost:5051';
const PROD_ENDPOINT = 'https://api.steadyshuttle.com';
const BACKEND_ENDPOINT = process.env.NODE_ENV === 'development' ? DEV_ENDPOINT : PROD_ENDPOINT;
const KL_MIN_LEAD_DAYS = 3;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const getKlLeadTimeError = (dateStr) => {
  if (!dateStr) return '';
  const target = moment(dateStr, 'YYYY-MM-DD', true);
  if (!target.isValid()) return '';
  const today = moment().startOf('day');
  const daysAhead = target.diff(today, 'days');
  if (daysAhead <= 2) {
    const earliest = today.clone().add(KL_MIN_LEAD_DAYS, 'days');
    return `KL bookings require at least ${KL_MIN_LEAD_DAYS} days' notice. Please choose a departure date on or after ${earliest.format('YYYY-MM-DD')} (${earliest.format('ddd, DD MMM YYYY')}).`;
  }
  return '';
};

function Bookings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyAuth } = useContext(AuthContext);
  const { toggleNavBarVisibility, setSelectedKey } = useContext(NavBarContext);
  const { authInfo } = useContext(AuthInfoContext);
  const { appConfig } = useContext(ConfigContext);
  const klEnabled = appConfig['kl-enabled'] || process.env.NODE_ENV === 'development';
  // Initialize activeTab from URL params to avoid double-fetch on mount
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      return (tabParam === 'kl' || tabParam === 'shuttle') ? tabParam : 'shuttle';
    } catch (e) {
      return 'shuttle';
    }
  });
  const [klModalFetching, setKlModalFetching] = useState(false);
  // memoized datasets per tab
  const [shuttleData, setShuttleData] = useState(null);
  const [klData, setKlData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingShuttle, setLoadingShuttle] = useState(false);
  const [loadingKL, setLoadingKL] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSelfCancelModalVisible, setIsSelfCancelModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [passengerLoaded, setPassengerLoaded] = useState(false);
  const [formSubmissionLoading, setFormSubmissionLoading] = useState(false);
  const [hoursUntilDeparture, setHoursUntilDeparture] = useState(0);
  const [refundType, setRefundType] = useState(0);
  const [refundValue, setRefundValue] = useState(0);
  const [otherSelected, setOtherSelected] = useState(false);
  const [customDateConfirmed, setCustomDateConfirmed] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const customDateInputRef = useRef(null);
  const customDateSectionRef = useRef(null);
  const [fetchingRefundValue, setFetchingRefundValue] = useState(false);
  const [showAddTimeslots, setShowAddTimeslots] = useState(false);
  const [newTimeslotValues, setNewTimeslotValues] = useState([]);
  const [selectedPassengerId, setSelectedPassengerId] = useState(null);
  const [selectedDateValue, setSelectedDateValue] = useState(null);

  const [cost, setCost] = useState(0);
  const [klPricing, setKlPricing] = useState(null);
  const [klPricesLoading, setKlPricesLoading] = useState(false);
  const [klClass, setKlClass] = useState('Standard');
  const [klFallbackToStandard, setKlFallbackToStandard] = useState(false); // For Business: book Standard if Business unavailable
  const [klSeatPrefs, setKlSeatPrefs] = useState(['window', 'aisle']); // ['window', 'aisle']
  const [klWheelchair, setKlWheelchair] = useState(false);
  const [klOkuAcknowledged, setKlOkuAcknowledged] = useState(false);
  const [formErrors, setFormErrors] = useState([]); // Track form validation errors
  const [klLeadTimeError, setKlLeadTimeError] = useState('');

  // Payment choice modal state (shown after booking creation)
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [paymentChoiceData, setPaymentChoiceData] = useState(null);
  // paymentChoiceData = { bookingId, paymentUrl, bookingType, summary }

  // DEV ONLY: Persist KL form state across hot reloads
  const DEV_STORAGE_KEY = '__kl_booking_dev_state__';
  const devRestoredRef = useRef(false);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    // Restore state on mount
    try {
      const saved = localStorage.getItem(DEV_STORAGE_KEY);
      if (saved) {
        const s = JSON.parse(saved);
        if (s.isModalVisible) {
          devRestoredRef.current = true; // Mark that we're restoring with modal open
        }
        if (s.selectedDirection !== undefined) setSelectedDirection(s.selectedDirection);
        if (s.selectedPassengerId) setSelectedPassengerId(s.selectedPassengerId);
        if (s.selectedDateValue) setSelectedDateValue(s.selectedDateValue);
        if (s.otherSelected) setOtherSelected(s.otherSelected);
        if (s.customDate) setCustomDate(s.customDate);
        if (s.customDateConfirmed) setCustomDateConfirmed(s.customDateConfirmed);
        if (s.klClass) setKlClass(s.klClass);
        if (s.klFallbackToStandard !== undefined) setKlFallbackToStandard(s.klFallbackToStandard);
        if (s.klSeatPrefs) setKlSeatPrefs(s.klSeatPrefs);
        if (s.klWheelchair !== undefined) setKlWheelchair(s.klWheelchair);
        if (s.isModalVisible) setIsModalVisible(true);
        // Restore form values after a longer tick to ensure modal is mounted
        setTimeout(() => {
          if (formRef.current && s.formValues) {
            formRef.current.setFieldsValue(s.formValues);
            console.log('[DEV] Restored form values:', s.formValues);
          }
          devRestoredRef.current = false; // Clear flag after restoration
        }, 200);
        console.log('[DEV] Restored KL form state from localStorage');
      }
    } catch (e) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DEV ONLY: Save state on changes
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (activeTab !== 'kl') return;
    const state = {
      selectedDirection,
      selectedPassengerId,
      selectedDateValue,
      otherSelected,
      customDate,
      customDateConfirmed,
      klClass,
      klFallbackToStandard,
      klSeatPrefs,
      klWheelchair,
      isModalVisible,
      formValues: formRef.current?.getFieldsValue(),
    };
    localStorage.setItem(DEV_STORAGE_KEY, JSON.stringify(state));
  }, [activeTab, selectedDirection, selectedPassengerId, selectedDateValue, otherSelected, customDate, customDateConfirmed, klClass, klFallbackToStandard, klSeatPrefs, klWheelchair, isModalVisible]);

  const fetchKlPricing = async () => {
    try {
      setKlPricesLoading(true);
      const res = await axios.get(BACKEND_ENDPOINT + '/api/kl-pricing');
      if (res.data && res.data.success) setKlPricing(res.data);
    } catch (e) {
      // ignore; UI will continue to show missing pricing
    } finally {
      setKlPricesLoading(false);
    }
  };
  // Prefetch on mount only if KL tab is initially active
  useEffect(() => {
    if (activeTab === 'kl' && !klPricing) fetchKlPricing();
    // fetchKlPricing is safe to omit to avoid refetch loop; activeTab guards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync activeTab from URL (?tab=kl|shuttle) so hard refresh retains selection.
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tabParam = params.get('tab');
      if ((tabParam === 'kl' || tabParam === 'shuttle') && tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
    } catch (e) {
      // ignore malformed querystrings
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const updateTabParam = (tabKey) => {
    try {
      const params = new URLSearchParams(location.search);
      params.set('tab', tabKey);
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    if (activeTab !== 'kl' || !isModalVisible) {
      setKlLeadTimeError('');
      return;
    }
    const dateStr = otherSelected ? (customDateConfirmed ? customDate : null) : selectedDateValue;
    if (!dateStr || dateStr === '0000-00-00') {
      setKlLeadTimeError('');
      return;
    }
    setKlLeadTimeError(getKlLeadTimeError(dateStr));
  }, [activeTab, isModalVisible, otherSelected, customDate, customDateConfirmed, selectedDateValue]);

  const klTotal = (dirVal, cls) => {
    if (!klPricing) return 0;
    const dirKey = (dirVal === 0) ? 'jb_to_kl' : 'kl_to_jb';
    const base = (klPricing[dirKey] && klPricing[dirKey]['standard']) || 0;
    const addon = (klPricing[dirKey] && klPricing[dirKey]['business']) || 0;
    return base + (cls === 'Business' ? addon : 0);
  };

  const [addTimeslotsForm] = Form.useForm();

  const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(false);
  const [timeslotAvailability, setTimeslotAvailability] = useState({});
  const [klTrainsByKey, setKlTrainsByKey] = useState({}); // key: `${date}-${direction}` -> trains[]
  const [klTimeslotStatus, setKlTimeslotStatus] = useState(null); // 'confirmed' | 'pending_release' | null

  const formRef = useRef(null);

  const handleAddTimeslotValues = (value) => {
    setNewTimeslotValues(value)
  }

  const fetchPassengers = () => {
    axios.get(BACKEND_ENDPOINT + '/api/passengers', {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        setPassengers(response.data.passengers);
        setPassengerLoaded(true);
      })
      .catch((error) => {
        console.error('Error fetching passengers:', error);
      });
  };

  const fetchBookings = (tab = activeTab) => {
    const endpoint = tab === 'kl' ? '/api/kl-bookings' : '/api/bookings';
    if (tab === 'kl') setLoadingKL(true); else setLoadingShuttle(true);
    axios.get(BACKEND_ENDPOINT + endpoint, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        const data = response.data.bookings.map((booking) => {
          // Normalize KL bookings to have same fields as shuttle bookings
          const normalized = {
            ...booking,
            key: booking._id,
          };
          // KL bookings use targetDateFull, shuttle uses targetDate
          if (tab === 'kl' && booking.targetDateFull && !booking.targetDate) {
            const dateMoment = moment(booking.targetDateFull);
            normalized.targetDate = dateMoment.format('D MMM YY'); // 2-digit year like shuttle
            normalized.targetDateObj = dateMoment;
            normalized.targetDayOfWeek = dateMoment.format('dddd');
          }
          // Normalize train field
          if (tab === 'kl' && booking.train) {
            normalized.targetTrain = booking.train;
          }
          return normalized;
        });
        if (tab === 'kl') {
          setKlData(data);
          if (activeTab === 'kl') setTableData(data);
        } else {
          setShuttleData(data);
          if (activeTab === 'shuttle') setTableData(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching bookings:', error);
      })
      .finally(() => {
        if (tab === 'kl') setLoadingKL(false); else setLoadingShuttle(false);
      });
  };

  const handlePatchTimeslots = async () => {
    // get selected booking id and new timeslot values
    const id = selectedBooking._id
    const newTimeslots = newTimeslotValues

    const formattedValues = {
      train: newTimeslots,
    };

    try {
      await axios.patch(BACKEND_ENDPOINT + '/api/bookings/add-timeslots/' + id, formattedValues, {
        headers: {
          cid: authInfo.cid,
          sess: authInfo.session_key,
        },
      });
      fetchBookings();
      setShowAddTimeslots(false)
      setSelectedBooking(null)
      message.success('Timeslots added successfully!');
    } catch (error) {
      console.error('Error adding timeslots:', error.response.data.error);
      message.error('Error: ' + error.response.data.error);
    }
  }

  const handleAddBooking = async (bookingDetails) => {
    console.log('[handleAddBooking] Called with:', bookingDetails);
    setFormErrors([]); // Clear validation errors on successful submit
    setFormSubmissionLoading(true);
    try {
      if (otherSelected && !customDateConfirmed) {
        message.error('Please confirm the custom date before submitting');
        setFormSubmissionLoading(false);
        return;
      }

      // Check OKU acknowledgment for KL bookings
      if (activeTab === 'kl' && klWheelchair && !klOkuAcknowledged) {
        message.error('Please acknowledge the wheelchair berth (OKU) notice before submitting');
        setFormSubmissionLoading(false);
        return;
      }

      const finalDate = otherSelected ? customDate : bookingDetails.date;

      if (activeTab === 'kl') {
        const leadError = getKlLeadTimeError(finalDate);
        if (leadError) {
          setKlLeadTimeError(leadError);
          message.error(leadError);
          setFormSubmissionLoading(false);
          return;
        }
        // Create KL booking in kl_bookings
        console.log('[KL Booking] Starting submission...');
        console.log('[KL Booking] bookingDetails:', bookingDetails);
        const priceObj = klPricing || {};
        const dirKey = (bookingDetails.direction === 0) ? 'jb_to_kl' : 'kl_to_jb';
        const base = priceObj[dirKey] ? priceObj[dirKey][(klClass || 'Standard').toLowerCase()] : null;
        // Handle train selection - if "all" is selected alone, store as ['any']
        let trainSelection = Array.isArray(bookingDetails.train) ? bookingDetails.train : [bookingDetails.train];
        const hasAllOption = trainSelection.includes('all');
        // Filter out "all" placeholder and keep specific train numbers
        const specificTrains = trainSelection.filter(t => t !== 'all');
        // If only "all" was selected (no specific trains), use ['any'] to indicate any train
        const finalTrainSelection = specificTrains.length > 0 ? specificTrains : ['any'];
        const payload = {
          date: finalDate,
          direction: bookingDetails.direction,
          passenger: bookingDetails.passenger,
          serviceClass: klClass,
          fallbackToStandard: klClass === 'Business' ? klFallbackToStandard : false,
          price: base,
          train: finalTrainSelection,
          is_custom_date: otherSelected,
          dateType: otherSelected ? 'custom' : 'standard',
          seatPreference: klWheelchair ? [] : klSeatPrefs,
          wheelchairAccessible: klWheelchair,
        };
        console.log('[KL Booking] Payload:', payload);
        const response = await axios.post(BACKEND_ENDPOINT + '/api/kl-bookings', payload, {
          headers: { cid: authInfo.cid, sess: authInfo.session_key },
        });
        console.log('[KL Booking] Submission successful', response.data);

        // Show payment choice modal with booking details
        const passengerObj = passengers.find(p => p._id === bookingDetails.passenger);
        setPaymentChoiceData({
          bookingId: response.data.booking._id,
          paymentUrl: response.data.booking.payment_url,
          bookingType: 'kl',
          summary: {
            route: bookingDetails.direction === 0 ? 'JB → KL' : 'KL → JB',
            date: finalDate,
            passenger: passengerObj?.fullName || passengerObj?.name || 'Unknown',
            class: klClass,
          }
        });

        formRef.current.resetFields();
        setKlClass('Standard');
        setKlFallbackToStandard(false);
        setKlSeatPrefs(['window', 'aisle']);
        setKlWheelchair(false);
        setKlOkuAcknowledged(false);
        setIsModalVisible(false);
        setShowPaymentChoice(true);
        setFormSubmissionLoading(false);
        return;
      }
      const formattedValues = {
        date: finalDate,
        direction: bookingDetails.direction,
        passenger: bookingDetails.passenger,
        train: bookingDetails.train,
        is_custom_date: otherSelected,
        dateType: otherSelected ? 'custom' : 'standard',
      };
      const response = await axios.post(BACKEND_ENDPOINT + '/api/bookings', formattedValues, {
        headers: {
          cid: authInfo.cid,
          sess: authInfo.session_key,
        },
      });
      console.log('[Shuttle Booking] Submission successful', response.data);

      // Show payment choice modal with booking details
      const passengerObj = passengers.find(p => p._id === bookingDetails.passenger);
      setPaymentChoiceData({
        bookingId: response.data._id,
        paymentUrl: response.data.payment_url,
        bookingType: 'shuttle',
        summary: {
          route: bookingDetails.direction === 0 ? 'To JB' : 'To SG',
          date: finalDate,
          passenger: passengerObj?.fullName || passengerObj?.name || 'Unknown',
        }
      });

      formRef.current.resetFields();
      setIsModalVisible(false);
      setShowPaymentChoice(true);
      setFormSubmissionLoading(false);
    } catch (error) {
      console.error('Error adding booking:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.reason || error.message || 'Failed to create booking';
      console.log('Booking error:', errorMsg);
      message.error(errorMsg);
      setFormSubmissionLoading(false);
    }
  };

  const fetchTimeslotAvailability = async (selectedDate, selectedDirection) => {
    if (!selectedDate || selectedDirection === undefined) return;
    
    setIsLoadingTimeslots(true);
    try {
      if (activeTab === 'kl') {
        const key = `${selectedDate}-${selectedDirection}`;
        const response = await axios.get(
          `${BACKEND_ENDPOINT}/api/kl-timeslots/${selectedDate}`,
          {
            headers: { cid: authInfo.cid, sess: authInfo.session_key },
            params: { direction: selectedDirection },
          }
        );
        if (response.data && response.data.success && response.data.timeslot) {
          const ts = response.data.timeslot;
          const trains = ts.trains || [];
          setKlTrainsByKey(prev => ({ ...prev, [key]: trains }));
          setKlTimeslotStatus(ts.status || (trains.length ? 'confirmed' : null));
        } else {
          console.error('Failed to fetch KL timeslot:', response.data?.error);
        }
      } else {
        const response = await axios.get(BACKEND_ENDPOINT + '/api/timeslot-availability', {
          headers: {
            cid: authInfo.cid,
            sess: authInfo.session_key
          },
          params: {
            date: selectedDate,
            direction: selectedDirection
          }
        });

        if (response.data.success) {
          setTimeslotAvailability(response.data.availability);
        } else {
          console.error('Failed to fetch timeslot availability:', response.data.error);
        }
      }
    } catch (error) {
      console.error('Error fetching timeslot availability:', error);
    } finally {
      setIsLoadingTimeslots(false);
    }
  };

  const prefetchKlTimeslots = async (direction = 0) => {
    if (!authInfo || !authInfo.cid || !authInfo.session_key) return;
    try {
      console.log(`[prefetchKlTimeslots] Fetching direction ${direction}...`);
      const res = await axios.get(`${BACKEND_ENDPOINT}/api/kl-timeslots`, {
        headers: { cid: authInfo.cid, sess: authInfo.session_key },
        params: { direction },
      });
      console.log(`[prefetchKlTimeslots] Response for direction ${direction}:`, res.data);
      if (res.data && res.data.success && Array.isArray(res.data.timeslots)) {
        const next = {};
        for (const ts of res.data.timeslots) {
          if (!ts?.date) continue;
          const dirVal = typeof ts.direction === 'number' ? ts.direction : direction;
          const key = `${ts.date}-${dirVal}`;
          next[key] = ts.trains || [];
        }
        console.log(`[prefetchKlTimeslots] Storing ${Object.keys(next).length} timeslots for direction ${direction}:`, Object.keys(next).slice(0, 5));
        if (Object.keys(next).length) {
          setKlTrainsByKey(prev => ({ ...prev, ...next }));
        }
      }
    } catch (e) {
      console.error(`[prefetchKlTimeslots] Error fetching direction ${direction}:`, e);
    }
  };

  // Effect to fetch availability when date or direction changes (shuttle only)
  useEffect(() => {
    if (activeTab === 'kl') return; // KL doesn't need timeslot availability
    const currentDate = formRef.current?.getFieldValue('date');
    const currentDirection = formRef.current?.getFieldValue('direction');
    if (currentDate && currentDirection !== undefined) {
      fetchTimeslotAvailability(currentDate, currentDirection);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef, activeTab]);

  // Update the train options rendering to include availability tags
  const renderTrainOptions = (trains) => {
    return trains.map(train => {
      const availability = timeslotAvailability[train.value] || { status: 'Unknown', color: 'default' };
      return (
        <Select.Option key={train.value} value={train.value} label={train.label}>
          <Space>
            {train.label}
            {isLoadingTimeslots ? (
              <Spin size="small" />
            ) : (
              <Tag color={availability.color}>{availability.status}</Tag>
            )}
          </Space>
        </Select.Option>
      );
    });
  };

  useEffect(() => {
    verifyAuth();
    setSelectedKey('3');
    toggleNavBarVisibility(true);
    // fetchBookings is handled by the tab change useEffect below
    return () => {
      toggleNavBarVisibility(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On tab change: show memoized data immediately if present, otherwise empty and fetch; show spinner while loading. Also fetch KL pricing if needed.
  useEffect(() => {
    if (activeTab === 'kl') {
      if (klEnabled) {
        if (klData !== null) setTableData(klData); else setTableData([]);
        fetchBookings('kl');
        if (!klPricing) fetchKlPricing();
      } else {
        setTableData([]);
      }
    } else {
      if (shuttleData !== null) setTableData(shuttleData); else setTableData([]);
      fetchBookings('shuttle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, klEnabled]);

	  useEffect(() => {
	    if (isModalVisible) {
	      fetchPassengers();

	      // Skip reset if we're restoring from dev state (hot reload)
	      if (devRestoredRef.current) {
	        console.log('[DEV] Skipping modal reset - restoring from localStorage');
	        return;
	      }

	      // reset progressive flow when modal opens
	      const defaultDir = activeTab === 'kl' ? 0 : null;
	      setSelectedDirection(defaultDir);
	      setSelectedPassengerId(null);
	      setSelectedDateValue(null);
	      setOtherSelected(false);
	      setCustomDate('');
	      setCustomDateConfirmed(false);
	      setFormErrors([]); // Clear any previous validation errors
	      if (activeTab === 'kl') {
	        setKlClass('Standard');
        setKlFallbackToStandard(false);
	        setKlSeatPrefs(['window', 'aisle']);
	        setKlWheelchair(false);
	        setKlOkuAcknowledged(false);
	        setCost(klTotal(0, 'Standard'));
	      } else {
	        setCost(0);
	      }
	      // Set form values after a tick to ensure form is mounted
	      setTimeout(() => {
	        formRef.current?.setFieldsValue({
	          direction: defaultDir !== null ? defaultDir : undefined,
	          passenger: undefined,
	          date: undefined,
	          train: [],
	          customDate: undefined,
	          serviceClass: 'Standard',
	          seatPrefs: ['window', 'aisle'],
	          wheelchair: false,
	        });
	      }, 50);
	    }
	    // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [isModalVisible]);

  // Sync selectedDirection state with form's initialValues for KL
  // (Form uses initialValues={{ direction: 0 }} for KL, so we just need to sync the visual state)
  useEffect(() => {
    if (!isModalVisible) return;
    if (activeTab !== 'kl') return;
    if (klModalFetching || !klPricing) return;
    if (devRestoredRef.current) return;

    // Ensure selectedDirection matches the form's initial value
    if (selectedDirection === null) {
      setSelectedDirection(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible, activeTab, klModalFetching, klPricing]);

  const isTabLoading = (activeTab === 'kl')
    ? (loadingKL || klData === null)
    : (loadingShuttle || shuttleData === null);
  const isKlDisabled = activeTab === 'kl' && !klEnabled;
  const hasDirection = selectedDirection === 0 || selectedDirection === 1;
  const hasPassenger = !!selectedPassengerId;
  const hasDate = (!otherSelected && !!selectedDateValue) || (otherSelected && customDateConfirmed);



  const columns = [
    {
      title: 'Date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      defaultSortOrder: 'descend',
      sorter: (a, b) => moment(a.targetDateObj).unix() - moment(b.targetDateObj).unix(),
      render: (text, record) => (
        <Space>
          <Text>{text}{record.targetDayOfWeek && ", " + record.targetDayOfWeek.substring(0, 3)}</Text>
          {(record.isCustomDate || record.is_custom_date || record.dateType === 'custom') && (
            <Tag color="orange">Custom</Tag>
          )}
        </Space>
      )
    },
    {
      title: (<>
        <Tooltip title="Hover over the train number tags to view the actual timing.">
          Train <ExclamationCircleOutlined style={{
            marginLeft: '0.3em'
          }} />
        </Tooltip>
      </>),
      dataIndex: 'targetTrain',
      key: 'targetTrain',
      render: (text, record) => (
        <Space style={{ display: 'flex', flexWrap: 'wrap' }}>
          {Array.isArray(text) ? text.map((train) => {
            // For KL bookings, look up train info from klTrainsByKey
            if (activeTab === 'kl') {
              const key = record.targetDateFull && record.targetDirection !== undefined
                ? `${record.targetDateFull}-${record.targetDirection}`
                : null;
              const trains = key ? (klTrainsByKey[key] || []) : [];
              const klTrainInfo = trains.find(t => (t.trainNo || t.train_no) === train);

              if (train === 'any') {
                return <Tag key={record._id + train} color="blue">Any</Tag>;
              }

              if (klTrainInfo) {
                const dep = klTrainInfo.departTime || klTrainInfo.depart_time || '';
                const arr = klTrainInfo.arriveTime || klTrainInfo.arrive_time || '';
                const tooltipContent = (
                  <div>
                    <div>Train #{train}</div>
                    <div>Depart: {dep}</div>
                    <div>Arrive: {arr}</div>
                  </div>
                );
                return (
                  <Tooltip key={record._id + train} title={tooltipContent}>
                    <Tag>{dep || train}</Tag>
                  </Tooltip>
                );
              }
              return (
                <Tooltip key={record._id + train} title={`Train #${train}`}>
                  <Tag>{train}</Tag>
                </Tooltip>
              );
            }

            // For shuttle bookings
            const trainInfo = [
              ...trainsTowardsJohor,
              ...trainsTowardsSingapore,
            ].find(t => t.value === train);
            return (
              <Tooltip key={record._id + train} title={trainInfo?.label || train}>
                <Tag>{train}</Tag>
              </Tooltip>
            );
          }) : (text ? <Tag>{text}</Tag> : <Tag color="blue">Any</Tag>)}
        </Space>
      )
    },
    {
      title: 'Direction',
      dataIndex: 'targetDirection',
      key: 'targetDirection',
      // defaultSortOrder: 'ascend',
      sorter: (a, b) => a.targetDirection - b.targetDirection,
      render: (text) => {
        if (activeTab === 'kl') {
          // KL: 0 = JB -> KL, 1 = KL -> JB
          return text === 0
            ? (<Tag color="green">JB <ArrowRightOutlined /> KL</Tag>)
            : (<Tag color="orange">KL <ArrowRightOutlined /> JB</Tag>);
        }
        // Shuttle: 0 = SG -> JB, 1 = JB -> SG
        return text === 1
          ? (<Tag color="orange">SG <ArrowLeftOutlined /> JB</Tag>)
          : (<Tag color="green">SG <ArrowRightOutlined /> JB</Tag>);
      },
    },
    // Seat Class column - only for KL bookings
    ...(activeTab === 'kl' ? [{
      title: 'Class',
      dataIndex: 'serviceClass',
      key: 'serviceClass',
      render: (text, record) => {
        const serviceClass = record.serviceClass;
        const fallback = record.fallbackToStandard;
        const isBooked = record.status === 'booked';

        // For booked bookings, show the actual class that was booked
        // For pending bookings, show what was requested (including "Best Available")
        if (!isBooked && serviceClass === 'Business' && fallback) {
          return <Tag color="purple">Best Available</Tag>;
        } else if (serviceClass === 'Business') {
          return <Tag color="gold">Business</Tag>;
        } else {
          return <Tag color="blue">Standard</Tag>;
        }
      },
    }] : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      // defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        switch (a.status) {
          case 'working':
            return -1;
          case 'reviewing':
            return b.status === 'working' ? 1 : -1;
          case 'payment_pending':
            return b.status === 'working' || b.status === 'reviewing' ? 1 : -1;
          case 'booked':
            return b.status === 'user_cancelled' || b.status === 'admin_cancelled' || b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'user_cancelled':
            return b.status === 'admin_cancelled' || b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'admin_cancelled':
            return b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'refunded':
            return b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'cancelled':
            return b.status === 'rejected' ? -1 : 1;
          case 'rejected':
            return 1;
          default:
            return 0;
        }
      },
      filters: [
        {
          text: 'Working',
          value: 'working',
        },
        {
          text: 'Booked',
          value: 'booked',
        },
        {
          text: 'Cancelled',
          // all cancelled statuses
          value: ['user_cancelled', 'admin_cancelled', 'refunded', 'cancelled', 'rejected'],
        },
      ],
      render: (text, record) => {
        if (text === "working") {
          return <Tooltip title={("Booking ID: " + record._id)}>
            <Tag icon={<SyncOutlined spin />} color="blue">Worker Running</Tag>
          </Tooltip>;
        } else if (text === "reviewing") {
          return <Tooltip title={("Booking ID: " + record._id)}>
            <Tag icon={<ClockCircleOutlined />} color="default">Pending Review</Tag>
          </Tooltip>;
        } else if (text === "paused") {
          return <Tag icon={<PauseCircleOutlined />} color="warning">Paused</Tag>;
        } else if (text === "payment_pending") {
          return <Tag icon={<ClockCircleOutlined />} color="gold">Pending Payment</Tag>;
        } else if (text === "queued") {
          return <Tooltip title={"We've put this booking into the queue. Queueing bookings links them together and helps prevent your passengers from being booked onto different train timeslots."}><Tag icon={<SyncOutlined />} color="gold">In Queue <QuestionCircleOutlined/></Tag></Tooltip>;
        } else if (text === "booked") {
          return <Tag icon={<CheckCircleOutlined />} color="success">Booked</Tag>;
        } else if (text === "user_cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled By You</Tag>;
        } else if (text === "admin_cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled By Admin</Tag>;
        } else if (text === "refunded") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Refunded</Tag>;
        } else if (text === "cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled</Tag>;
        } else if (text === "rejected") {
          return (<Link to="/support"><Tag icon={<ExclamationCircleOutlined />} color="error">Rejected - Contact Support</Tag>
          </Link>);
        }
      }
    },
    {
      title: 'Passenger',
      dataIndex: ['passenger', 'name'],
      key: ['passenger', 'name'],
      sorter: (a, b) => a.passenger.name.localeCompare(b.passenger.name),
      render: (text, record) => (
        <Space>
          <Tooltip title={(
            <div>
              <Text style={{ color: "#fff" }}>Name: {record.passenger.name}</Text>
              <br />
              <Text style={{ color: "#fff" }}>Passport: {record.passenger.passport}</Text>
              <br />
              <Text style={{ color: "#fff" }}>Contact: {record.passenger.contact}</Text>
            </div>
          )}>

            <Text>{record.passenger.name.split(' ')[0] + '...'}</Text> <EyeOutlined />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <>
          {/* <Button onClick={() => handleDelete(record._id)}>Delete</Button> */}
          {
            record.status === "payment_pending" &&
            (<Button onClick={() => handlePayment(record._id)} style={{ marginRight: '1em', marginBottom: '1em'}}>Make Payment</Button>)
          }
          {
            ((record.status === "reviewing") || (record.status === "payment_pending")) &&
            (<Button onClick={() => handleDelete(record._id)}>Cancel</Button>)
          }
          {
            (record.status === "working" || record.status === "queued") &&
            ((record.self_cancellable === false) ?
            (
              <Button onClick={() => alert("Self cancelling is only available for bookings created after 31 July 2023 under the new policy changes. Please contact support.")}>Cancel</Button>
            ) :
            (
              <div>
              {record.status === "working" && <Button onClick={() => handleAddTimeslots(record._id)} disabled={false} style={{ marginRight: '1em', marginBottom: '1em'}}>Add Timeslots</Button>}
              <Button onClick={() => handleSelfCancel(record._id)} disabled={fetchingRefundValue}>Cancel & Refund</Button>
              </div>
            ) )
          }
        </>
      ),
    },
  ];

  const handleAddTimeslots = (id) => {
    const data = (activeTab === 'kl' ? klData : shuttleData) || [];
    const booking = data.find((b) => b._id === id);
    setSelectedBooking(booking);
    setNewTimeslotValues(booking.targetTrain);
    addTimeslotsForm.setFieldsValue({
      train: booking.targetTrain
    });
    setShowAddTimeslots(true);
  };

  const handleSelfCancel = (id) => {
    // filter passenger from passengers based on matching id
    setFetchingRefundValue(true)
    const data = (activeTab === 'kl' ? klData : shuttleData) || [];
    setSelectedBooking(data.filter((b) => b._id === id)[0])
    // load data from endpoint - use KL endpoint for KL bookings
    const cancelEndpoint = activeTab === 'kl' ? '/api/cancel-kl-booking/' : '/api/cancel-booking/';
    axios.get(BACKEND_ENDPOINT + cancelEndpoint + id, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        const { hours_until_departure, refund_type, refund_value } = response.data;
        console.log(`Hours until departure: ${hours_until_departure}`);
        console.log(`Refund type: ${refund_type}`);
        console.log(`Refund value: ${refund_value}`);

        setHoursUntilDeparture(hours_until_departure)
        setRefundType(refund_type)
        setRefundValue(refund_value)
        setFetchingRefundValue(false)
        setIsSelfCancelModalVisible(true)
      })
      .catch((error) => {
        console.error('Error getting refund information:', error);
      });
  };


  const handleDelete = (id) => {
    // filter passenger from passengers based on matching id
    const data = (activeTab === 'kl' ? klData : shuttleData) || [];
    setSelectedBooking(data.filter((b) => b._id === id)[0])
    setIsDeleteModalVisible(true)
  };

  const handleDeleteConfirm = () => {
    console.log(`Deleting passenger ${selectedBooking.name} (${selectedBooking.passport})`);

    message.info('Cancelling booking..');
    const deleteEndpoint = activeTab === 'kl' ? '/api/kl-bookings/' : '/api/bookings/';
    axios.delete(BACKEND_ENDPOINT + deleteEndpoint + selectedBooking._id, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        // console.log(response)
        fetchBookings();
        message.success('Booking cancelled successfully!');
      })
      .catch((error) => {
        console.error('Error deleting booking:', error);
      });

    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  const handleSelfCancelCancel = () => {
    setIsSelfCancelModalVisible(false);
    setRefundValue(-1)
  }

  const handleSelfCancelConfirm = () => {
    // setRefundValue(-1)
    setFetchingRefundValue(true)

    // Use KL endpoint for KL bookings
    const cancelEndpoint = activeTab === 'kl' ? '/api/cancel-kl-booking/' : '/api/cancel-booking/';
    axios.post((BACKEND_ENDPOINT + cancelEndpoint + selectedBooking._id), {}, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        const { status } = response.data;
        if (status === 'ok') {
          message.success("Successfully cancelled & refunded")
        } else {
          message.info(status)
        }
        fetchBookings()
        setFetchingRefundValue(false)
      })
      .catch((error) => {
        console.error('Error getting refund information:', error);
      });
    setIsSelfCancelModalVisible(false);
  }

  const handlePayment = (id) => {
    // get booking object from bookings based on matching id
    const data = (activeTab === 'kl' ? klData : shuttleData) || [];
    let payment_booking = data.filter((b) => b._id === id)[0]
    // open payment_booking.payment_url in new tab
    window.open(payment_booking.payment_url, "_blank")
  };



  // const getWeekendDates = () => {
  //   const today = moment();
  //   const nextFriday = moment().day(5 + (today.day() > 5 ? 7 : 0));
  //   const nextSaturday = nextFriday.clone().add(1, 'day');
  //   const nextSunday = nextFriday.clone().add(2, 'day');
  //   const nextNextFriday = nextFriday.clone().add(7, 'day');
  //   const nextNextSaturday = nextSaturday.clone().add(7, 'day');
  //   const nextNextSunday = nextSunday.clone().add(7, 'day');
  //   return [nextFriday, nextSaturday, nextSunday, nextNextFriday, nextNextSaturday, nextNextSunday];
  // };

  const getWeekendDates = () => {
    const weekends = [];
    const num_weekends = 52  // 1 year of weekends
    for (let i = 0; i < (num_weekends + 1); i++) { // 
      const nextFriday = moment().day(5).add(7 * i, 'day');
      // const nextFriday = moment().day(5 + (today.day() > 5 ? 7 : 0)).add(7 * i, 'day'); // disable after 5th day
      const nextSaturday = nextFriday.clone().add(1, 'day');
      const nextSunday = nextFriday.clone().add(2, 'day');
      weekends.push(nextFriday, nextSaturday, nextSunday); 
    }
    
    return weekends;
  };
  

  const trainsTowardsJohor = [
    { label: "8.30AM (Train #72)", value: 72 },
    { label: "9.45AM (Train #74)", value: 74 },
    { label: "11.00AM (Train #76)", value: 76 },
    { label: "12.30PM (Train #78)", value: 78 },
    { label: "1.45PM (Train #80)", value: 80 },
    { label: "3.00PM (Train #82)", value: 82 },
    { label: "4.15PM (Train #84)", value: 84 },
    { label: "5.30PM (Train #86)", value: 86 },
    { label: "6.45PM (Train #88)", value: 88 },
    { label: "8.00PM (Train #90)", value: 90 },
    { label: "9.15PM (Train #92)", value: 92 },
    { label: "10.30PM (Train #94)", value: 94 },
    { label: "11.45PM (Train #96)", value: 96 }
  ];

  const trainsTowardsSingapore = [
    { label: "5.00AM (Train #61)", value: 61 },
    { label: "5.30AM (Train #63)", value: 63 },
    { label: "6.00AM (Train #65)", value: 65 },
    { label: "6.30AM (Train #67)", value: 67 },
    { label: "7.00AM (Train #69)", value: 69 },
    { label: "7.30AM (Train #71)", value: 71 },
    { label: "8.45AM (Train #73)", value: 73 },
    { label: "10.00AM (Train #75)", value: 75 },
    { label: "11.30AM (Train #77)", value: 77 },
    { label: "12.45PM (Train #79)", value: 79 },
    { label: "2.00PM (Train #81)", value: 81 },
    { label: "3.15PM (Train #83)", value: 83 },
    { label: "4.30PM (Train #85)", value: 85 },
    { label: "5.45PM (Train #87)", value: 87 },
    { label: "7.00PM (Train #89)", value: 89 },
    { label: "8.15PM (Train #91)", value: 91 },
    { label: "9.30PM (Train #93)", value: 93 },
    { label: "10.45PM (Train #95)", value: 95 }
  ];



  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minHeight: '70vh' }}>
      <div style={{ textAlign: 'left', width: '100%' }}>
        <div style={{ marginBottom: 12 }}>
          <Tabs
            activeKey={activeTab}
            onChange={(k) => {
              setActiveTab(k);
              updateTabParam(k);
            }}
            items={[
              { key: 'shuttle', label: 'Shuttle' },
              { key: 'kl', label: <>KL <Tag color="green" style={{ marginLeft: 4, verticalAlign: 'middle' }}>Beta</Tag></> }
            ]}
          />
        </div>
        {activeTab === 'kl' && klEnabled && (
          <Alert
            type="info"
            showIcon
            message={<span>Bookings for JB Sentral to KL Sentral now available! Get your tickets here.</span>}
            style={{ marginBottom: 16 }}
          />
        )}
        <Title level={1} style={{ marginTop: '0em' }}>
          {activeTab === 'kl' ? (
            <span role="img" aria-label="kl-bookings">🚆⚡ KL Bookings <Tag color="green" style={{ verticalAlign: 'middle' }}>Beta</Tag></span>
          ) : (
            <><FileTextOutlined /> Shuttle Bookings</>
          )}
        </Title>
        <Space style={{ marginBottom: 16 }}>
	          <Button
	            type="primary"
	            onClick={async () => {
	              if (activeTab === 'kl') {
	                if (!klEnabled) return;
	                setIsModalVisible(true);
	                setKlModalFetching(true);
	                const tasks = [];
	                if (!klPricing) tasks.push(fetchKlPricing());
	                // Prefetch timeslots for both directions
	                tasks.push(prefetchKlTimeslots(0));
	                tasks.push(prefetchKlTimeslots(1));
	                try {
	                  await Promise.all(tasks);
	                } finally {
	                  setKlModalFetching(false);
	                }
	                return;
	              }
	              setIsModalVisible(true);
	            }}
	            disabled={activeTab === 'kl' && !klEnabled}
	            style={{ minWidth: 200, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
	          >
	            <FileAddOutlined /> {activeTab === 'kl' ? 'Add KL Booking' : 'Add Shuttle Booking'}
	          </Button>
        </Space>
        {isKlDisabled ? (
          <Alert
            type="warning"
            showIcon
            message={<span>Stay tuned! SteadyShuttle for KL ETS is coming soon.</span>}
            style={{ marginTop: 16 }}
          />
        ) : (
        <>
        {isTabLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
            <Spin size="large" tip="Loading bookings..." />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={columns}
              dataSource={tableData}
              rowKey="_id"
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}
	        <Modal
	          title={<><FileAddOutlined /> {activeTab === 'kl' ? 'Add KL Booking' : 'Add Shuttle Booking'}</>}
	          // visible={isModalVisible}
	          open={isModalVisible}
	          onCancel={() => setIsModalVisible(false)}
	          footer={null}
	        >
	          <div>
	            {activeTab === 'kl' && (klModalFetching || !klPricing) ? (
	              <div
	                style={{
	                  display: 'flex',
	                  flexDirection: 'column',
	                  alignItems: 'center',
	                  justifyContent: 'center',
	                  minHeight: 200,
	                  padding: '40px 0',
	                }}
	              >
	                <Spin size="large" />
	                <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>Fetching available trains..</Paragraph>
	              </div>
	            ) : (
	            <div>
	          <Form
	            layout="vertical"
	            ref={formRef}
	            initialValues={{
	              direction: activeTab === 'kl' ? 0 : undefined,
	              train: [],
	            }}
	            onFinish={handleAddBooking}
	            onFinishFailed={(errorInfo) => {
	              console.log('[Form] Validation failed:', errorInfo);
	              console.log('[Form] Failed fields:', errorInfo.errorFields);
	              console.log('[Form] Current values:', errorInfo.values);
	              // Extract error messages for display near submit button
	              const errors = errorInfo.errorFields.map(f => f.errors[0]).filter(Boolean);
	              setFormErrors(errors);
	            }}
	          >

            <Form.Item
              name="direction"
              label="Direction"
              rules={[
                {
                  required: true,
                  message: 'Please choose a direction',
                },
              ]}
            >
              {(() => {
                const handleDirectionChange = (value) => {
                  formRef.current.setFieldsValue({ direction: value, train: [] });
                  setSelectedDirection(value);
                  setFormErrors([]); // Clear validation errors when user interacts

                  if (activeTab === 'kl') {
                    // KL: Only reset train selection (direction-specific), keep passenger/date
                    setCost(klTotal(value, klClass || 'Standard'));
                  } else {
                    // Shuttle: Reset everything as before
                    setSelectedPassengerId(null);
                    setSelectedDateValue(null);
                    setOtherSelected(false);
                    setCustomDate('');
                    setCustomDateConfirmed(false);
                    formRef.current.setFieldsValue({ passenger: undefined, date: undefined, customDate: undefined });
                    setCost(value === 0 ? 9.4 : 8.8);
                    // Fetch timeslot availability for shuttle
                    const date = formRef.current.getFieldValue('date');
                    if (date && date !== '0000-00-00') {
                      fetchTimeslotAvailability(date, value);
                    }
                  }
                };

                const DirectionButton = ({ value, icon, title, subtitle, isSelected }) => (
                  <div
                    onClick={() => handleDirectionChange(value)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 8px',
                      borderRadius: 8,
                      border: isSelected ? '2px solid #1890ff' : '2px solid #d9d9d9',
                      backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ fontSize: 24, color: isSelected ? '#1890ff' : '#595959', marginBottom: 4 }}>
                      {icon}
                    </div>
                    <div style={{ fontWeight: isSelected ? 600 : 500, color: isSelected ? '#1890ff' : '#262626', fontSize: 14 }}>
                      {title}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                      {subtitle}
                    </div>
                  </div>
                );

                return (
                  <div style={{ display: 'flex', gap: 12 }}>
                    {activeTab === 'kl' ? (
                      <>
                        <DirectionButton
                          value={0}
                          icon={<ArrowUpOutlined />}
                          title="Towards KL"
                          subtitle={klPricing?.jb_to_kl?.standard ? `$${klPricing.jb_to_kl.standard}` : '—'}
                          isSelected={selectedDirection === 0}
                        />
                        <DirectionButton
                          value={1}
                          icon={<ArrowDownOutlined />}
                          title="Towards JB"
                          subtitle={klPricing?.kl_to_jb?.standard ? `$${klPricing.kl_to_jb.standard}` : '—'}
                          isSelected={selectedDirection === 1}
                        />
                      </>
                    ) : (
                      <>
                        <DirectionButton
                          value={0}
                          icon={<ArrowUpOutlined />}
                          title="Towards Johor"
                          subtitle="$9.40"
                          isSelected={selectedDirection === 0}
                        />
                        <DirectionButton
                          value={1}
                          icon={<ArrowDownOutlined />}
                          title="Towards Singapore"
                          subtitle="$8.80"
                          isSelected={selectedDirection === 1}
                        />
                      </>
                    )}
                  </div>
                );
              })()}
            </Form.Item>

            {activeTab === 'kl' && (
              <Form.Item name="serviceClass" label="Seat Class">
                {(() => {
                  const handleClassChange = (value) => {
                    setKlClass(value);
                    // Reset fallback when switching classes
                    if (value !== 'Business') {
                      setKlFallbackToStandard(false);
                    }
                    const dir = formRef.current.getFieldValue('direction');
                    const dirKey = (dir === 0) ? 'jb_to_kl' : 'kl_to_jb';
                    const p = (klPricing && klPricing[dirKey]) ? klPricing[dirKey][value.toLowerCase()] : 0;
                    setCost(p || 0);
                  };

                  // ETS only has Standard (Coaches B-F) and Business (Coach A)
                  // Blue for Standard, Gold for Business, Purple for Best Available
                  const blueColor = '#1890ff';
                  const goldColor = '#d48806';
                  const purpleColor = '#722ed1';

                  const seatClasses = [
                    {
                      value: 'Standard',
                      label: 'Standard Only',
                      price: klPricing ? `$${(klPricing.jb_to_kl?.standard || 0).toFixed(2)}` : 'Loading...',
                      description: 'Coaches B-F, forward-facing seats',
                      color: blueColor,
                      bgGradient: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                    },
                    {
                      value: 'Business',
                      label: 'Business Only',
                      price: klPricing ? `$${(klPricing.jb_to_kl?.business || 0).toFixed(2)} (+$${(klPricing.business_upgrade || 0).toFixed(2)} upgrade)` : 'Loading...',
                      description: 'Coach A, larger seats',
                      color: goldColor,
                      bgGradient: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)',
                    },
                  ];

                  return (
                    <>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {seatClasses.map((sc) => {
                          const isSelected = klClass === sc.value && !klFallbackToStandard;
                          return (
                            <div
                              key={sc.value}
                              onClick={() => {
                                handleClassChange(sc.value);
                                setKlFallbackToStandard(false);
                              }}
                              style={{
                                flex: 1,
                                padding: '12px 8px',
                                borderRadius: 8,
                                border: isSelected ? `2px solid ${sc.color}` : '2px solid #d9d9d9',
                                background: isSelected ? sc.bgGradient : '#fff',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <div style={{
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? sc.color : '#595959',
                                fontSize: 14,
                              }}>
                                {sc.label}
                              </div>
                              <div style={{
                                fontSize: 11,
                                color: isSelected ? sc.color : '#8c8c8c',
                                marginTop: 2,
                              }}>
                                {sc.price}
                              </div>
                              <div style={{
                                fontSize: 10,
                                color: isSelected ? sc.color : '#8c8c8c',
                                marginTop: 2,
                                opacity: 0.8,
                              }}>
                                {sc.description}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Full-width "Best Available" option */}
                      <div
                        onClick={() => {
                          handleClassChange('Business');
                          setKlFallbackToStandard(true);
                        }}
                        style={{
                          marginTop: 8,
                          padding: '12px',
                          borderRadius: 8,
                          border: klFallbackToStandard ? `2px solid ${purpleColor}` : '2px solid #d9d9d9',
                          background: klFallbackToStandard ? 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)' : '#fff',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div style={{
                          fontWeight: klFallbackToStandard ? 600 : 400,
                          color: klFallbackToStandard ? purpleColor : '#595959',
                          fontSize: 14,
                        }}>
                          Best Available ⚡
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: klFallbackToStandard ? purpleColor : '#8c8c8c',
                          marginTop: 2,
                        }}>
                          {klPricing ? `$${(klPricing.jb_to_kl?.business || 0).toFixed(2)}` : 'Loading...'}
                        </div>
                        <div style={{
                          fontSize: 10,
                          color: klFallbackToStandard ? purpleColor : '#8c8c8c',
                          marginTop: 2,
                          opacity: 0.8,
                        }}>
                          {klPricing ? `Books first seat we can find. $${(klPricing.business_upgrade || 0).toFixed(2)} refund if Standard.` : ''}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </Form.Item>
            )}

            {activeTab === 'kl' && (
              <Form.Item name="seatPrefs" label="Seat Preference">
                {(() => {
                  const seatOptions = [
                    { value: 'window', label: 'Window', icon: <BorderOuterOutlined /> },
                    { value: 'aisle', label: 'Aisle', icon: <ColumnWidthOutlined /> },
                  ];

                  const toggleSeatPref = (value) => {
                    // If wheelchair is selected, unselect it when window/aisle is clicked
                    if (klWheelchair) {
                      setKlWheelchair(false);
                      setKlOkuAcknowledged(false);
                    }
                    if (klSeatPrefs.includes(value)) {
                      setKlSeatPrefs(klSeatPrefs.filter(p => p !== value));
                    } else {
                      setKlSeatPrefs([...klSeatPrefs, value]);
                    }
                  };

                  const toggleWheelchair = () => {
                    const newVal = !klWheelchair;
                    setKlWheelchair(newVal);
                    if (newVal) {
                      setKlSeatPrefs([]);
                    } else {
                      setKlSeatPrefs(['window', 'aisle']);
                      setKlOkuAcknowledged(false);
                    }
                  };

                  return (
                    <>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        {seatOptions.map((opt) => {
                          const isSelected = klSeatPrefs.includes(opt.value);
                          return (
                            <div
                              key={opt.value}
                              onClick={() => toggleSeatPref(opt.value)}
                              style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '10px 8px',
                                borderRadius: 8,
                                border: isSelected ? '2px solid #1890ff' : '2px solid #d9d9d9',
                                backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <div style={{ fontSize: 20, color: isSelected ? '#1890ff' : '#595959', marginBottom: 2 }}>
                                {opt.icon}
                              </div>
                              <div style={{ fontWeight: isSelected ? 600 : 400, color: isSelected ? '#1890ff' : '#595959', fontSize: 13 }}>
                                {opt.label}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Wheelchair option as separate button */}
                      <div
                        onClick={toggleWheelchair}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: klWheelchair ? '2px solid #faad14' : '2px solid #d9d9d9',
                          backgroundColor: klWheelchair ? '#fffbe6' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <span style={{ fontSize: 18 }}>♿</span>
                        <span style={{ fontWeight: klWheelchair ? 600 : 400, color: klWheelchair ? '#d48806' : '#595959', fontSize: 13 }}>
                          Wheelchair Berth (OKU)
                        </span>
                      </div>
                      {klWheelchair && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 8 }}
                          message="Wheelchair Berth (OKU) Notice"
                          description={
                            <>
                              <div>This seat type is reserved for persons with disabilities. It is a wheelchair berth designed for securing a wheelchair only. Please only select this option if you require wheelchair accessible seating.</div>
                              <div style={{ marginTop: 12 }}>
                                <Checkbox
                                  checked={klOkuAcknowledged}
                                  onChange={(e) => setKlOkuAcknowledged(e.target.checked)}
                                >
                                  I fully understand the details regarding this seat type, and will not be able to make further changes after payment is made.
                                </Checkbox>
                              </div>
                            </>
                          }
                        />
                      )}
                    </>
                  );
                })()}
              </Form.Item>
            )}
            {
              passengers.length === 0  && passengerLoaded &&
              <a href="/passengers" target="_blank">
              <p style={{ color: 'red', marginBottom: "1em"}}>It seems you have not added any passengers to your account yet. You can do so by clicking here.</p>
              </a>
            }
            {hasDirection ? (
              <Form.Item
                name="passenger"
                label="Passenger"
                rules={[
                  {
                    required: true,
                    message: 'Please choose a passenger',
                  },
                ]}
              >
                <Select
                  popupClassName="booking-select-dropdown"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  optionFilterProp="label"
                  loading={!authInfo}
                  onChange={(value) => {
                    formRef.current.setFieldsValue({ passenger: value });
                    setSelectedPassengerId(value);
                  }}
                >
                  {passengers.map((passenger) => (
                    <Option key={passenger._id} value={passenger._id} label={passenger.name}>
                      {passenger.name} ({passenger.passport})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Paragraph style={{ color: 'red' }}>Please select a direction to proceed.</Paragraph>
            )}
            {hasDirection && hasPassenger ? (
              <Form.Item
                name="date"
                label="Departure Date"
                rules={[
                  {
                    required: true,
                    message: 'Please choose a date',
                  },
                ]}
              >
                <Select
                  popupClassName="booking-select-dropdown"
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                    </div>
                  )}
                  onChange={(value) => {
                    setSelectedDateValue(value);
                    if (activeTab === 'kl') {
                      if (value && value !== "0000-00-00") {
                        setKlLeadTimeError(getKlLeadTimeError(value));
                      } else {
                        setKlLeadTimeError('');
                      }
                    }
                    if (value === "0000-00-00") {
                      setOtherSelected(true);
                      setCustomDate('');
                      setCustomDateConfirmed(false);
                      setTimeout(() => {
                        customDateInputRef.current?.focus();
                        customDateSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 10);
                    } else {
                      setOtherSelected(false);
                      setCustomDate('');
                      setCustomDateConfirmed(false);
                      // Only fetch timeslot availability for shuttle (KL doesn't need it)
                      if (activeTab !== 'kl') {
                        const direction = formRef.current.getFieldValue('direction');
                        if (direction !== undefined) {
                          fetchTimeslotAvailability(value, direction);
                        }
                      }
                    }
                  }}
                >

                  <Option key="other" value="0000-00-00">
                    All Other Dates -{" "}
                    <Tooltip title={activeTab === 'kl'
                      ? "Custom dates require additional processing and monitoring until KTMB releases the schedule."
                      : "This option incurs an additional cost because it requires manual processing."}>
                      +{activeTab === 'kl' ? `$${klPricing?.custom_date_fee || 5}` : '50¢'} <QuestionCircleOutlined />
                    </Tooltip>
                  </Option>
                  {getWeekendDates().map((date) => (
                    <Option key={date.format('YYYY-MM-DD')} value={date.format('YYYY-MM-DD')}>
                      {date.format('ddd, DD MMM YYYY')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              hasDirection ? (
                <Paragraph style={{ color: 'red' }}>Please select a passenger to proceed.</Paragraph>
              ) : null
            )}


            {otherSelected && (
              <div ref={customDateSectionRef} style={{ marginTop: -8, marginBottom: 16, padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <Form.Item
                  name="customDate"
                  label="Custom Date"
                  rules={[
                    { required: true, message: 'Please enter a custom date' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const isValid = moment(value, 'YYYY-MM-DD', true).isValid();
                        if (!isValid) return Promise.reject('Use YYYY-MM-DD format');
                        if (moment(value).isBefore(moment().startOf('day'))) {
                          return Promise.reject('Date must be today or later');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    ref={customDateInputRef}
                    placeholder="YYYY-MM-DD"
                    onChange={() => {
                      setCustomDateConfirmed(false);
                      if (activeTab === 'kl') setKlLeadTimeError('');
                    }}
                  />
                </Form.Item>
                <Button
                  type="default"
                  onClick={() => {
                    formRef.current
                      ?.validateFields(['customDate'])
                      .then(() => {
                        const val = formRef.current.getFieldValue('customDate');
                        if (!moment(val, 'YYYY-MM-DD', true).isValid()) {
                          message.error('Use YYYY-MM-DD format');
                          setCustomDateConfirmed(false);
                          return;
                        }
                        const oneYearAhead = moment().add(1, 'year').endOf('day');
                        if (moment(val).isAfter(oneYearAhead)) {
                          message.error('Custom date must be within the next year');
                          setCustomDateConfirmed(false);
                          return;
                        }
                        if (moment(val).isBefore(moment().startOf('day'))) {
                          message.error('Date must be today or later');
                          setCustomDateConfirmed(false);
                          return;
                        }
                        if (activeTab === 'kl') {
                          const leadError = getKlLeadTimeError(val);
                          if (leadError) {
                            setKlLeadTimeError(leadError);
                            message.error(leadError);
                            setCustomDateConfirmed(false);
                            return;
                          }
                          setKlLeadTimeError('');
                        }

                        setCustomDate(val);
                        setCustomDateConfirmed(true);

                        // Only fetch timeslot availability for shuttle (KL doesn't need it)
                        if (activeTab !== 'kl') {
                          const direction = formRef.current.getFieldValue('direction');
                          if (direction !== undefined) {
                            fetchTimeslotAvailability(val, direction);
                          }
                        }

                        message.success(`Custom date confirmed: ${moment(val).format('D MMMM YYYY (dddd)')}`);
                      })
                      .catch(() => {
                        setCustomDateConfirmed(false);
                      });
                  }}
                  style={{ width: '100%' }}
                >
                  Confirm Date
                </Button>
                {customDateConfirmed && customDate && (
                  <Paragraph style={{ color: 'green', marginTop: 10, marginBottom: 0 }}>
                    <CheckCircleOutlined style={{ marginRight: 6 }} />
                    {moment(customDate).format('D MMMM YYYY (dddd)')}
                  </Paragraph>
                )}
                {!customDateConfirmed && (
                  <Paragraph style={{ color: '#faad14', marginTop: 10, marginBottom: 0, fontSize: 13 }}>
                    Please confirm the custom date to proceed.
                  </Paragraph>
                )}
              </div>
            )}

            {activeTab === 'kl' && klLeadTimeError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="Departure date too soon"
                description={klLeadTimeError}
              />
            )}

            {(hasDate && hasPassenger && hasDirection) ? (
              <>
                <Form.Item
                  name="train"
                  label="Train"
                  rules={[{ required: true, message: 'Please choose a train' }]}
                  style={{
                    marginBottom: "0.5em"
                  }}
                >
                  <Select
                    mode="multiple"
                    popupClassName="booking-select-dropdown"
                    placeholder="Select preferred train(s)"
                    loading={activeTab !== 'kl' && isLoadingTimeslots}
                    disabled={activeTab !== 'kl' && isLoadingTimeslots}
                    optionLabelProp="label"
                    onChange={(values, option) => {
                      if (activeTab === 'kl' && values && values.length > 0) {
                        const currentValues = formRef.current?.getFieldValue('train') || [];
                        const hadAll = currentValues.includes('all');
                        const hasAll = values.includes('all');
                        const specificTrains = values.filter(v => v !== 'all');

                        // If "all" was just added, clear all specific trains
                        if (hasAll && !hadAll) {
                          setTimeout(() => {
                            formRef.current?.setFieldsValue({ train: ['all'] });
                          }, 0);
                          return;
                        }
                        // If a specific train was added while "all" was selected, remove "all"
                        if (hadAll && specificTrains.length > 0) {
                          setTimeout(() => {
                            formRef.current?.setFieldsValue({ train: specificTrains });
                          }, 0);
                          return;
                        }
                      }
                    }}
	                  >
	                    {activeTab === 'kl'
	                      ? (() => {
	                          const d = otherSelected ? customDate : selectedDateValue;
	                          const key = (d && (selectedDirection === 0 || selectedDirection === 1)) ? `${d}-${selectedDirection}` : null;
	                          const trains = key ? (klTrainsByKey[key] || []) : [];
	                          console.log('[KL Trains] date:', d, 'direction:', selectedDirection, 'key:', key, 'trains:', trains.length, 'klTrainsByKey keys:', Object.keys(klTrainsByKey));
	                          const isPending = klTimeslotStatus === 'pending_release';
	                          const options = [];

	                          // Always add "All Timeslots" option first
	                          options.push(
	                            <Option key="all" value="all" label="All Available Timeslots">
	                              All Available Timeslots {isPending && <Tag color="blue" style={{ marginLeft: 4 }}>Recommended</Tag>}
	                            </Option>
	                          );

	                          // Add individual train options (even if "all" is selected, so user can switch)
	                          if (trains.length > 0) {
	                            trains.forEach((t) => {
	                              const tn = t.trainNo || t.train_no || t.value;
	                              const dep = t.departTime || t.depart_time || '';
	                              const arr = t.arriveTime || t.arrive_time || '';
	                              const cat = t.serviceCategory || t.service_category || '';
	                              const isTbc = t.tbc === true;
	                              // Simple string label for the selected tag display
	                              const labelText = `${dep}${arr ? ` → ${arr}` : ''} (#${tn})`;
	                              const labelContent = (
	                                <>
	                                  {dep}{arr ? ` → ${arr}` : ''} (Train #{tn}){cat ? ` • ${cat}` : ''}
	                                  {isTbc && <Tag color="orange" style={{ marginLeft: 6 }}>TBC</Tag>}
	                                </>
	                              );
	                              options.push(
	                                <Option key={tn} value={tn} label={labelText}>{labelContent}</Option>
	                              );
	                            });
	                          }

	                          return options;
	                        })()
	                      : (selectedDirection === 0
	                          ? renderTrainOptions(trainsTowardsJohor)
	                          : renderTrainOptions(trainsTowardsSingapore))}
	                  </Select>
	                </Form.Item>
                {activeTab === 'kl' && klTimeslotStatus === 'pending_release' && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16, marginTop: 8 }}
                    message="Schedule not yet released"
                    description="KTMB hasn't released the schedule for this date yet. Timings shown with TBC are estimates based on historical data. We recommend selecting 'All Available Timeslots' to maximise your chances."
                  />
                )}
                <Text style={{
                  fontSize: "0.8em",
                  color: "#969696",
                  marginTop: "-1em",
                }}>
                  {activeTab === 'kl'
                    ? "Select 'All Available Timeslots' to book any available train, or choose specific trains to target. TBC timings are estimates until KTMB releases the official schedule."
                    : "Choose several train slots to increase the likelihood of getting a ticket quickly. We'll book the first available train among those selected."}
                </Text>
              </>
            ) : (
              hasDirection && hasPassenger && !otherSelected ? (
                <Paragraph style={{ color: 'red' }}>Please select a departure date to proceed.</Paragraph>
              ) : null
            )}
            <Divider />

            <Form.Item style={{ marginBottom: "0" }}>
              <Text strong>Total Cost: S${(cost + (otherSelected ? (activeTab === 'kl' ? (klPricing?.custom_date_fee || 5) : 0.5) : 0)).toFixed(2)}</Text>
              <Paragraph>
                <Text style={{
                  fontSize: "0.8em",
                  color: "#969696"
                }}>Next Steps: Your booking request will be reviewed by our team, and you'll be notified to make payment after it has been approved.</Text>
              </Paragraph>
            </Form.Item>
            <Form.Item
            >
              <Checkbox required>
               I have read and agreed to the terms of creating a booking request outlined in the <a href={activeTab === 'kl' ? '/faq?tab=kl' : '/faq'} target='_blank'>FAQs</a>, including the cancellation policy.
              </Checkbox>
            </Form.Item>

            <Form.Item
            >
              <Checkbox required>
                I understand that a full refund is only possible if my booking request is not fulfilled at departure time. Early cancellations will incur <a href={activeTab === 'kl' ? '/faq?tab=kl' : '/faq'} target='_blank'>cancellation charges</a>, which will be deducted from the refund amount.
              </Checkbox>
            </Form.Item>
            {/* {
              isLastMinuteBooking && 
              <Form.Item
              >
                <Checkbox required>
                I understand that for last-minute bookings (made less than 48 hours from departure) that end up cancelled, whether by me or due to unfulfillment, there will be a small deduction of 20¢ from the refund amount.
                <Tooltip title="This deduction is intended to cover administrative costs associated with cancellations. It is also imposed to ensure fair use of our service and to prevent abuse."><Tag color='blue' style={{ marginLeft: '0.5em'}}>Why?</Tag></Tooltip>
                </Checkbox>
              </Form.Item>
            } */}
            {/* {
              isWeekendBooking &&
              <Form.Item
              >
              <Checkbox required>
                I understand that the SteadyShuttle support team is only available on weekdays. Therefore, any cancellation or refund requests made on weekends will be processed on the following Monday.
              </Checkbox>
              </Form.Item>
            } */}
            {formErrors.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="Please fix the following errors:"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {formErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                }
              />
            )}
            <Form.Item style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div
                  onClick={(e) => {
                    if (formSubmissionLoading) return;
                    console.log('[Submit Button] Clicked');
                    console.log('[Submit Button] activeTab:', activeTab);
                    console.log('[Submit Button] formSubmissionLoading:', formSubmissionLoading);
                    console.log('[Submit Button] Form values:', formRef.current?.getFieldsValue());
                    formRef.current?.submit();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '2px solid #1890ff',
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    cursor: formSubmissionLoading ? 'not-allowed' : 'pointer',
                    opacity: formSubmissionLoading ? 0.6 : 1,
                    fontWeight: 600,
                    fontSize: 15,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {formSubmissionLoading ? (
                    <><SyncOutlined spin /> Processing...</>
                  ) : (
                    <><CheckCircleOutlined /> Confirm Booking Details</>
                  )}
                </div>
                <div
                  onClick={() => setIsModalVisible(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '14px 16px',
                    borderRadius: 8,
                    border: '2px solid #d9d9d9',
                    backgroundColor: '#fff',
                    color: '#595959',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: 15,
                    transition: 'all 0.2s ease',
                  }}
                >
                  Cancel
                </div>
              </div>
            </Form.Item>
	          </Form>
	            </div>
	            )}
	          </div>
	        </Modal>

        <Modal
          title={<><FileExcelOutlined /> Confirm Cancellation & Refund?</>}
          // visible={isDeleteModalVisible}
          open={isSelfCancelModalVisible}
          onCancel={handleSelfCancelCancel}
          footer={[
            <Button key="delete" type="primary" danger onClick={handleSelfCancelConfirm} disabled={fetchingRefundValue}>
              Confirm Cancel & Refund
            </Button>,
            <Button key="cancel" onClick={handleSelfCancelCancel} disabled={fetchingRefundValue}>
              Go Back
            </Button>,
          ]}
        >
          <Paragraph>
            <b><ExclamationCircleOutlined /> Please read up on our cancellation policy and its relevant charges before proceeding, this action is NOT reversible.</b>
          </Paragraph>


          {/* <Paragraph><b>{hoursUntilDeparture}</b> hours left until first selected departure for booking.</Paragraph> */}
          <Paragraph><b>{hoursUntilDeparture}</b> hours left until last selected departure for booking.</Paragraph>

          <div style={{
            fontWeight: 'bold',
            color: 'red',
          }}>
          {
            refundType === 0 ? 
            (
              <Paragraph style={{ 
                color: 'red',
              }}>Cancellation Charge: ${refundValue/100}</Paragraph>
            ) :
            (
              <Paragraph style={{ 
                color: 'red',
              }}>Cancellation Charge: {refundValue}% of original booking amount</Paragraph>
            )
          }
          </div>


          <Paragraph>
            Are you sure you want to cancel booking for {selectedBooking?.passenger.name} ({selectedBooking?.passenger.passport}) on {selectedBooking?.targetDate} (Train {selectedBooking?.targetTrain})?
          </Paragraph>
        </Modal>
        </>
        )}

        <Modal
          title={<><FileExcelOutlined /> Confirm Cancellation</>}
          // visible={isDeleteModalVisible}
          open={isDeleteModalVisible}
          onCancel={handleDeleteCancel}
          footer={[
            <Button key="delete" type="primary" danger onClick={handleDeleteConfirm}>
              Confirm Cancellation
            </Button>,
            <Button key="cancel" onClick={handleDeleteCancel}>
              Go Back
            </Button>,
          ]}
        >
          <Paragraph>
            Are you sure you want to cancel booking for {selectedBooking?.passenger.name} ({selectedBooking?.passenger.passport}) on {selectedBooking?.targetDate} (Train {selectedBooking?.targetTrain})?
          </Paragraph>

          <Paragraph>
            <ExclamationCircleOutlined /> This action cannot be reversed.
          </Paragraph>
        </Modal>


        <Modal
          title={<>Add Timeslots<br/>{selectedBooking?.passenger.name} ({selectedBooking?.passenger.passport}) on {selectedBooking?.targetDate} ({selectedBooking?.targetDayOfWeek?.substring(0,3)})</>}
          open={showAddTimeslots}
          onCancel={() => {
            setShowAddTimeslots(false);
            setNewTimeslotValues([]);
            setSelectedBooking(null);
            addTimeslotsForm.resetFields();
          }}
          onOk={() => {
            addTimeslotsForm.validateFields()
              .then(values => {
                if (values.train.toString() === selectedBooking?.targetTrain.toString()) {
                  alert("Please select additional timeslots to continue.");
                } else {
                  setNewTimeslotValues(values.train);
                  setShowAddTimeslots(false);
                  handlePatchTimeslots();
                }
              })
              .catch(info => {
                console.log('Validate Failed:', info);
              });
          }}
        >
          <Form
            form={addTimeslotsForm}
            layout="vertical"
            initialValues={{
              train: selectedBooking?.targetTrain || []
            }}
          >
            <Paragraph>
              Select the timeslots you'd like to add to your booking. Changes will affect bookings that are in queue as well.
            </Paragraph>

            <Paragraph style={{
              color: 'red'
            }}>
              Newly added timeslots cannot be removed after they have been confirmed.
            </Paragraph>
            
            <Form.Item
              name="train"
              label="Train"
              rules={[{ required: true, message: 'Please choose a train' }]}
              style={{ marginBottom: "0.5em" }}
            >
              <Select
                mode="multiple"
                placeholder="Please select timeslots"
                onChange={handleAddTimeslotValues}
              >
                {selectedBooking?.targetDirection === 0
                  ? trainsTowardsJohor.map((train) => (
                    <Select.Option
                      key={train.value}
                      value={train.value}
                      disabled={Array.isArray(selectedBooking?.targetTrain) && selectedBooking.targetTrain.includes(train.value)}
                    >
                      {train.label}
                    </Select.Option>
                  ))
                  : trainsTowardsSingapore.map((train) => (
                    <Select.Option
                      key={train.value}
                      value={train.value}
                      disabled={Array.isArray(selectedBooking?.targetTrain) && selectedBooking.targetTrain.includes(train.value)}
                    >
                      {train.label}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* Payment Choice Modal - shown after booking creation */}
        <Modal
          open={showPaymentChoice}
          closable={false}
          footer={null}
          centered
          width={400}
        >
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Booking Created!"
            subTitle={
              paymentChoiceData && (
                <div style={{ textAlign: 'left', marginTop: 16 }}>
                  <p><strong>Route:</strong> {paymentChoiceData.summary.route}</p>
                  <p><strong>Date:</strong> {paymentChoiceData.summary.date}</p>
                  <p><strong>Passenger:</strong> {paymentChoiceData.summary.passenger}</p>
                  {paymentChoiceData.summary.class && (
                    <p><strong>Class:</strong> {paymentChoiceData.summary.class}</p>
                  )}
                </div>
              )
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              onClick={() => {
                window.open(paymentChoiceData.paymentUrl, '_blank');
                setShowPaymentChoice(false);
                setPaymentChoiceData(null);
                fetchBookings(activeTab);
              }}
              style={{ height: 48 }}
            >
              Pay Now
            </Button>
            <Button
              size="large"
              onClick={() => {
                setShowPaymentChoice(false);
                setPaymentChoiceData(null);
                fetchBookings(activeTab);
              }}
              style={{ height: 48 }}
            >
              Back to Bookings
            </Button>
          </div>
          <p style={{
            marginTop: 16,
            fontSize: 12,
            color: '#888',
            textAlign: 'center'
          }}>
            Payment link expires in 24 hours. You can also pay from the bookings list.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default Bookings;
